import {Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {NestedTreeControl} from "@angular/cdk/tree";
import {MatTreeNestedDataSource} from "@angular/material/tree";
import {ProdAppInjectorService} from 'prod-shared';
import {first} from "rxjs";
import {OrganizationTreeSelect} from "../../dip/organization-tree-select";
import {RoleService} from "../../services/role.service";
import {Role} from "../../dip/role";
import {OrganizationTypeName} from "../../enum/organization-type";
import {TranslateService} from "@ngx-translate/core";

export interface TreeNode {
  id?: number;
  name: string;
  isRoot?: boolean;
  children?: TreeNode[];
}

@Component({
  selector: 'app-tree-select',
  templateUrl: './tree-select.html',
  styleUrls: ['./tree-select.scss']
})
export class TreeSelect implements OnInit {
  @Input() dataService;
  @Input() isExpanded: boolean = false;
  @ViewChild('treeWrapper') treeElement: ElementRef;
  @ViewChild('treeInput') treeInput: ElementRef;

  treeControl = new NestedTreeControl<TreeNode>(node => node.children);
  treeDataSource = new MatTreeNestedDataSource<TreeNode>();
  filteredDataSource = new MatTreeNestedDataSource<TreeNode>()
  showTree: boolean = false;
  selectedNode: TreeNode;
  mainFilterValue: string = '';
  secondaryFilterValue: string = '';

  constructor(private organizationTreeSelect: OrganizationTreeSelect, private roleService: RoleService, private translate: TranslateService) {
  }

  ngOnInit(): void {
    const isSystem = this.roleService.hasRole([Role.ROLE_SYS]);
    this.getData(isSystem);

    this.organizationTreeSelect.organizationTreeSelect$.subscribe(value => {
      this.setSelectedNode(value)
    });

    this.organizationTreeSelect.organizationTreeUpdate$.subscribe(() =>{
      this.getData(isSystem);
    })
  }

  hasChild = (_: number, node: TreeNode) => !!node.children && node.children.length > 0;

  getData(isSystem: boolean) {
    ProdAppInjectorService.injector.get(this.dataService).getTreeData(isSystem).pipe(first()).subscribe(
      data => {
        this.treeDataSource.data = this.filteredDataSource.data = data;
        this.treeDataSource.data = this.filteredDataSource.data = data;
        this.treeControl.dataNodes = this.treeDataSource.data;
        this.setSelectedNode(+this.organizationTreeSelect.organizationTreeSelect$.value);
        if (this.isExpanded) this.treeControl.expandAll();
      });
  }

  setSelectedNode(id: number) {
    this.selectedNode = this.findNodeById(this.treeControl.dataNodes, id);
  }

  onChangeFilter() {
    const value1 = typeof this.mainFilterValue === 'string' ? this.mainFilterValue.toLowerCase().trim() : '';
    const value2 = typeof this.secondaryFilterValue === 'string' ? this.secondaryFilterValue.toLowerCase().trim() : '';
    this.filteredDataSource.data = this.filterTree(this.filterTree(this.treeDataSource.data, value1, true), value2);
    this.treeControl.dataNodes = this.filteredDataSource.data;
    this.treeControl.expandAll();
  }

  filterTree(array, text, startsWith: boolean = false) {
    const getNodes = (result, object) => {
      if (startsWith ? object.name.toLowerCase().startsWith(text) : object.name.toLowerCase().includes(text)) {
        result.push(object);
        return result;
      }
      if (Array.isArray(object.children)) {
        const children = object.children.reduce(getNodes, []);
        if (children.length) result.push({...object, children});
      }
      return result;
    };
    return array.reduce(getNodes, []);
  }

  findNodeById(array, id) {
    if (!array) return null;
    let stack = [...array], node, i;

    while (stack.length > 0) {
      node = stack.pop();
      if (node.id === id) {
        return node;
      } else if (node.children && node.children.length) {
        for (i = 0; i < node.children.length; i += 1) {
          stack.push(node.children[i]);
        }
      }
    }
    return null;
  }

  onSelectNode(node) {
    this.showTree = false;
    this.organizationTreeSelect.organizationTreeSelect$.next(node.id);
  }

  onBlurInput(event) {
    const target = event.relatedTarget;
    if (!target || !this.treeElement.nativeElement.contains(target) && this.treeInput.nativeElement !== target) {
      this.showTree = false;
      this.treeInput.nativeElement.value = this.getDisplayValue();
      this.secondaryFilterValue = '';
    }
  }

  getDisplayValue() {
    const node = this.selectedNode;

    if (!node) return '';

    if (node['organizationType'].id === 3) return node.name;
    if (node['organizationType'].id === 1) return node.name + ' / ' + this.translate.instant('tree_select.all_sites');
    if (node['organizationType'].id === 2) return node['organization'].name + ' / ' + node.name;

    return node.name;
  }

  getIcon(node) {
    if (node.organizationType) {
      if (node.organizationType.name === OrganizationTypeName.DISTRIBUTOR) return 'business_center';
      if (node.organizationType.name === OrganizationTypeName.COMPANY) return 'other_houses';
      if (node.organizationType.name === OrganizationTypeName.SITE) return 'house';
    }
    return 'folder';
  }

  getInputWidth() {
    return this.treeInput ? this.treeInput.nativeElement.value.length * 10 + 'px' : '0px';
  }
}
